'use client'

// Next.js
import Link from 'next/link'

// Animation
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

// Icons
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

// design-studio
import {
  Body,
  Container,
  fadeInUp,
  H3,
  ImandraXLogo,
  ImandraXMonoMark,
  Lead,
  Section,
  SectionHeader,
  Spotlight,
  staggerContainer,
} from 'design-studio'

// Utils
import { Join } from 'design-studio/lib'

const FEATURES = [
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Real-world industrial applications in safety critical industries',
    description:
      'ImandraX is widely used in safety-critical industries such as financial markets, robotics and autonomy, and model-based systems engineering in government and defense.',
    href: 'https://imandrax.dev',
  },
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Formal verification',
    description:
      'Mathematical verification of algorithm properties with powerful reasoning automation powered by ImandraX -- bringing formal proofs and counterexamples seamlessly into the software engineering workflow.',
    href: 'https://www.imandra.ai/formal-verification',
  },
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Region decomposition',
    description:
      'Inspired by Cylindrical Algebraic Decomposition, this technique decomposes algorithm state-spaces into regions where system behavior is invariant, effectively computing a map of all possible system behaviors.',
    href: 'https://imandrax.dev',
  },
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Seamless integration of bounded and unbounded verification',
    description:
      "Seamlessly combine bounded model-checking and unbounded inductive proof through ImandraX. Every goal can be subjected to both bounded verification (complete for counterexamples in a precise sense) as well as unbounded verification, e.g., using Imandra's decision procedures and higher-order lifting of Boyer-Moore style automated induction.",
    href: 'https://imandrax.dev',
  },
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Counter-examples',
    description:
      'ImandraX is complete for counterexamples in a precise sense. Automatically synthesizes deep counterexamples to incorrect verification conjectures, even with recursive functions and nonlinear arithmetic, aiding discovery and eliminating time sinks from trying to prove false goals.',
    href: 'https://imandrax.dev',
  },
  {
    colspan: 'col-span-12 md:col-span-3',
    title: 'Imandra Discover',
    description:
      'Discover program properties and lemmas, extract logical patterns from data (e.g., logs) and synthesize them into executable and auditable models. Identify anomalies in out-of-sample data and generate corrective actions for detected issues.',
    href: 'https://imandrax.dev',
  },
]

export function UnderlyingTechnologies() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 })

  return (
    <Section
      id="technologies"
      className="font-display relative overflow-hidden py-12 md:mt-24 md:mb-32"
    >
      <Spotlight
        color="from-[#007e74]"
        className="top-[5%] left-0 z-10 mx-auto my-auto aspect-square h-[90%] w-full opacity-100"
      />
      <div
        ref={ref}
        className="absolute top-[5%] left-0 z-10 mx-auto my-auto aspect-square h-[90%] w-full opacity-90"
      >
        <ImandraXMonoMark
          className={Join(
            'absolute left-1/2 z-0 h-full w-auto origin-center -translate-x-1/2 text-[#2ea97e]',
            'scale-150 rotate-50 opacity-0 transition-all delay-300 duration-1000 ease-out',
            inView && 'scale-100! rotate-0! opacity-10!',
          )}
        />
        <ImandraXMonoMark
          className={Join(
            'absolute left-1/2 z-0 h-full w-auto origin-center -translate-x-1/2 text-[#2ea97e]',
            'scale-150 rotate-50 opacity-0 transition-all delay-300 duration-1000 ease-out',
            inView && 'scale-100! rotate-0! opacity-20!',
          )}
        />
        <ImandraXMonoMark
          className={Join(
            'absolute left-1/2 z-0 h-full w-auto origin-center -translate-x-1/2 text-[#2ea97e]',
            'scale-150 rotate-35 opacity-0 transition-all delay-300 duration-1000 ease-out',
            inView && 'scale-100! rotate-0! opacity-40!',
          )}
        />
        <ImandraXMonoMark
          className={Join(
            'absolute left-1/2 z-0 h-full w-auto origin-center -translate-x-1/2 text-[#2ea97e]',
            'scale-150 rotate-25 opacity-0 transition-all delay-150 duration-1000 ease-out',
            inView && 'scale-100! rotate-0! opacity-40!',
          )}
        />
        <ImandraXMonoMark
          className={Join(
            'absolute left-1/2 z-0 h-full w-auto origin-center -translate-x-1/2 text-[#2ea97e]',
            'scale-150 rotate-15 opacity-0 transition-all duration-1000 ease-out',
            inView && 'scale-100! rotate-0! opacity-40!',
          )}
        />
      </div>
      <Container className="relative z-20 max-w-6xl">
        <Spotlight
          color="from-teal-600/15"
          className="-top-200 -right-256 z-0 size-512"
        />
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px 0px' }}
        >
          <SectionHeader
            pretitle="Underlying Technologies"
            pretitleColor="text-cyan-500/80"
            srOnlyFullHeading="Underlying Technologies Neurosymbolic AI for Code"
            title={
              <>
                Neurosymbolic AI <br className="hidden lg:inline-block" /> for
                Code
              </>
            }
            subtitle="CodeLogician™ is powered by ImandraX, our award-winning automated reasoning engine that combines formal verification, symbolic reasoning, and rule synthesis to provide mathematical guarantees about your code's behavior."
            className="mb-9 pr-6"
            description={
              <>
                <div className="mt-6 flex flex-col items-start gap-6 lg:flex-row">
                  <Link
                    href="https://imandrax.dev"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit imandrax.dev"
                    className="group block w-full"
                  >
                    <ImandraXLogo className="h-auto w-full max-w-64 text-slate-900 dark:text-white" />
                  </Link>
                  <H3 className="mb-1">
                    Industrial automated reasoning engine
                  </H3>
                </div>
                <Lead size="md" tone="muted">
                  The latest version of our automated reasoning engine for
                  algorithm analysis, bringing unprecedented rigor and
                  automation to algorithm design and governance.
                </Lead>
              </>
            }
          />
        </motion.div>
      </Container>

      {/* Cards — wider container */}
      <Container className="max-w-8xl relative z-20 mt-3">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-100px 0px' }}
        >
          <motion.div
            className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            variants={fadeInUp}
          >
            {FEATURES.map((feature) => (
              <Link
                key={feature.title}
                href={feature.href!}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col rounded-2xl border border-slate-700/60 bg-slate-50/80 p-6 backdrop-blur-md transition-[border-color] duration-300 hover:border-teal-500/50 dark:bg-slate-800/30"
              >
                <H3 size="md" className="text-xl! lg:text-2xl!">
                  {feature.title}
                </H3>
                <Body size="sm" tone="muted" className="mb-4 font-light">
                  {feature.description}
                </Body>
                <div className="mt-auto flex items-center gap-1.5 font-mono text-sm text-slate-400 transition-colors group-hover:text-blue-300">
                  <ArrowTopRightOnSquareIcon className="size-2.5" />
                  Read more
                </div>
              </Link>
            ))}
          </motion.div>

          <motion.p
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px 0px' }}
            className="mx-auto max-w-6xl text-base text-slate-300"
          >
            Learn more about the reasoning engine at{' '}
            <a
              href="https://imandrax.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
            >
              imandrax.dev
            </a>
            .
          </motion.p>
        </motion.div>
      </Container>
    </Section>
  )
}
